import React, { useState, useEffect } from 'react';
import { apiCall, getUser } from '../lib/api.js';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, PieChart, Pie } from 'recharts';
import { CATEGORIES } from '../lib/scorecard-config.js';

const ATTR_MAP = {};
CATEGORIES.forEach(cat => cat.attributes.forEach(a => { ATTR_MAP[a.id] = a.label; }));
const BAND_COLORS = { Excellent: '#10b981', Good: '#3b82f6', Okay: '#f59e0b', 'Needs improvement': '#f97316', Failed: '#ef4444' };

export default function MyDashboardPage() {
  const user = getUser();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiCall('/my/stats')
      .then(d => { setStats(d); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;
  if (!stats || !stats.agent_name) return (
    <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
      <i className="ti ti-chart-bar text-3xl text-gray-300" aria-hidden="true"></i>
      <p className="text-gray-400 mt-2 text-sm">No data available yet.</p>
    </div>
  );

  const bandData = Object.entries(stats.rating_bands).filter(([,v]) => v > 0).map(([k, v]) => ({
    name: k, value: v, color: BAND_COLORS[k] || '#9ca3af',
  }));

  const weakData = stats.weak_spots.slice(0, 5).map(w => ({
    name: ATTR_MAP[w.id] || w.id, rate: Math.round(w.issue_rate * 100),
  }));

  const strengthData = stats.strengths.slice(0, 5).map(s => ({
    name: ATTR_MAP[s.id] || s.id, count: s.total,
  }));

  return (
    <div>
      <h2 className="text-lg font-medium mb-5">My performance</h2>

      {/* Hero stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-400">Average score</div>
          <div className="text-2xl font-medium mt-1">{Math.round(stats.avg_score * 100)}%</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-400">Total evaluations</div>
          <div className="text-2xl font-medium mt-1">{stats.total}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-400">This week</div>
          <div className="text-2xl font-medium mt-1">{stats.this_week}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-400">This month</div>
          <div className="text-2xl font-medium mt-1">{stats.this_month}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Weekly trend */}
        {stats.weekly_trend.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
              <i className="ti ti-trending-up text-purple-500" aria-hidden="true"></i> My weekly trend
            </h3>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={stats.weekly_trend.map(w => ({ ...w, score: Math.round(w.avg_score * 100) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={v => v + '%'} />
                <Tooltip formatter={(v, n) => n === 'score' ? v + '%' : v} />
                <Bar dataKey="score" radius={[4, 4, 0, 0]} maxBarSize={40}>
                  {stats.weekly_trend.map((w, i) => {
                    const s = Math.round(w.avg_score * 100);
                    return <Cell key={i} fill={s >= 95 ? '#10b981' : s >= 85 ? '#3b82f6' : s >= 70 ? '#f59e0b' : '#ef4444'} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Rating distribution */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
            <i className="ti ti-chart-pie text-purple-500" aria-hidden="true"></i> My ratings
          </h3>
          {bandData.length > 0 ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="50%" height={160}>
                <PieChart><Pie data={bandData} dataKey="value" cx="50%" cy="50%" innerRadius={35} outerRadius={60} paddingAngle={2}>
                  {bandData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie><Tooltip /></PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">{bandData.map(b => (
                <div key={b.name} className="flex items-center gap-2 text-xs">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: b.color }}></span>
                  <span className="text-gray-600">{b.name}</span><span className="font-medium">{b.value}</span>
                </div>
              ))}</div>
            </div>
          ) : <div className="text-center text-gray-400 text-sm py-8">No data</div>}
        </div>
      </div>

      {/* Weak spots & strengths side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Areas to improve */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
            <i className="ti ti-alert-triangle text-amber-500" aria-hidden="true"></i> Areas to improve
          </h3>
          {weakData.length > 0 ? (
            <div className="space-y-3">{weakData.map(w => (
              <div key={w.name}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-700 truncate max-w-[200px]" title={w.name}>{w.name}</span>
                  <span className="text-amber-600 font-medium">{w.rate}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-400 rounded-full" style={{ width: w.rate + '%' }}></div>
                </div>
              </div>
            ))}</div>
          ) : (
            <div className="text-center py-4">
              <i className="ti ti-star text-2xl text-emerald-400" aria-hidden="true"></i>
              <p className="text-sm text-emerald-600 mt-1">No weak spots — keep it up!</p>
            </div>
          )}
        </div>

        {/* Strengths */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
            <i className="ti ti-star text-emerald-500" aria-hidden="true"></i> Strengths
          </h3>
          {strengthData.length > 0 ? (
            <div className="space-y-2">{strengthData.map(s => (
              <div key={s.name} className="flex items-center gap-2 py-1.5">
                <i className="ti ti-check text-emerald-500 text-sm" aria-hidden="true"></i>
                <span className="text-sm text-gray-700 flex-1 truncate" title={s.name}>{s.name}</span>
                <span className="text-xs text-emerald-600 font-medium">{s.count}x Meets</span>
              </div>
            ))}</div>
          ) : (
            <div className="text-center text-gray-400 text-sm py-4">Keep scoring to see your strengths</div>
          )}
        </div>
      </div>
    </div>
  );
}
